import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { MessageBubble, MessageContainer } from "./ChatMessage.styled";
import { downloadCsv } from "./exportRowsToCsv";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT } from "./tokens";

export interface IAssistantContent {
  type: "text" | "table";
  text?: string;
  rows?: Array<Record<string, any>>;
  label?: string;
}

interface AssistantMessageProps {
  content: IAssistantContent | IAssistantContent[];
}

export function AssistantMessage({ content }: AssistantMessageProps) {
  const blocks = Array.isArray(content) ? content : [content];

  return (
    <MessageContainer>
      <Box sx={{ maxWidth: "100%", width: "100%" }}>
        <MessageBubble>
          {blocks.map((block, i) => (
            <AssistantBlock key={i} content={block} />
          ))}
        </MessageBubble>
      </Box>
    </MessageContainer>
  );
}

function AssistantBlock({ content }: { content: IAssistantContent }) {
  const { type, text, rows = [], label } = content;

  const renderTable = () => {
    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Nenhum dado encontrado.
        </Typography>
      );
    }

    const columns = Object.keys(rows[0]);

    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 400,
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "grey.50",
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  {column.replace(/_/g, " ").toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={index}
                hover
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell key={column} sx={{ py: 1.5 }}>
                    {row[column]?.toString() ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderExportButton = () => (
    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
      <Button
        size="small"
        variant="text"
        startIcon={<FileDownloadOutlinedIcon />}
        onClick={() => downloadCsv(rows)}
        sx={{
          fontFamily: INSIGHTS_FONT,
          fontWeight: 600,
          fontSize: 13,
          textTransform: "none",
          color: CONDUCCO_PRIMARY,
        }}
      >
        Exportar CSV
      </Button>
    </Box>
  );

  return (
    <Box sx={{ mb: type === "table" ? 1 : 0 }}>
      {label && (
        <Chip
          label={label}
          size="small"
          sx={{
            mb: 1.5,
            fontWeight: 600,
            fontFamily: "Sora, sans-serif",
            color: "#3024E1",
            backgroundColor: "rgba(48,36,225,0.08)",
          }}
        />
      )}

      {type === "text" && (
        <Typography
          variant="body1"
          sx={{ lineHeight: 1.6, fontFamily: "Sora, sans-serif", whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: formatMarkdownBold(text || "Não foi possível gerar uma resposta.") }}
        />
      )}

      {type === "table" && (
        <>
          {renderTable()}
          {rows.length > 0 && renderExportButton()}
        </>
      )}
    </Box>
  );
}

function formatMarkdownBold(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
}
